/**
 * Client service for sector operations rosters, shifts, duty roles, changes, and attendance.
 * R39 A6.a (plan docs/plans/sector-operations.md §2 SO-D5, SO-D14, SO-P7, SO-P16, SO-P60; §4 ops_shifts, ops_rosters, ops_roster_changes, ops_attendance; §6 A6.a).
 * Wraps callables for writes and listens to firestore collections for reads.
 */

import {
  collection,
  doc,
  query,
  where,
  orderBy,
  onSnapshot,
  type QueryConstraint,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import type { NlsKey } from '../nls/strings_nls'
import type {
  OpsDutyRole,
  OpsWorkspaceDepartmentsDoc,
  CreateDepartmentInput,
  RenameDepartmentInput,
  ArchiveDepartmentInput,
  OpsShift,
  OpsCoverageRule,
  OpsRoster,
  OpsRosterVersion,
  OpsRosterWarning,
  OpsRosterChange,
  OpsAttendance,
  OpsAttendanceCorrection,
  CreateDutyRoleInput,
  UpdateDutyRoleInput,
  CreateShiftInput,
  UpdateShiftInput,
  CreateCoverageRuleInput,
  UpdateCoverageRuleInput,
  CreateRosterInput,
  SaveDraftAssignmentsInput,
  PreviewRosterWarningsInput,
  PublishRosterInput,
  AcknowledgeRosterInput,
  RequestRosterChangeInput,
  RespondRosterChangeInput,
  DecideRosterChangeInput,
  CancelRosterChangeInput,
  MarkAttendanceRollInput,
  RequestAttendanceCorrectionInput,
  DecideAttendanceCorrectionInput,
  ImportAttendanceCsvInput,
  ImportAttendanceCsvResult,
  AssignReliefInput,
  AssignReliefResult,
} from '../types/ops'

// ---------------------------------------------------------------------------
// Callables (Writes)
// ---------------------------------------------------------------------------

export async function createDutyRole(input: CreateDutyRoleInput): Promise<OpsDutyRole> {
  return callable<CreateDutyRoleInput, OpsDutyRole>('createDutyRole', input)
}

export async function updateDutyRole(
  input: UpdateDutyRoleInput,
): Promise<{ success: true; key: string }> {
  return callable<UpdateDutyRoleInput, { success: true; key: string }>('updateDutyRole', input)
}

export async function archiveDutyRole(input: {
  workspaceId: string
  key: string
  opId?: string
}): Promise<{ success: true; key: string }> {
  return callable<{ workspaceId: string; key: string; opId?: string }, { success: true; key: string }>(
    'archiveDutyRole',
    input,
  )
}

export async function createDepartment(
  input: CreateDepartmentInput,
): Promise<{ success: true; key: string; version: number }> {
  return callable<CreateDepartmentInput, { success: true; key: string; version: number }>(
    'createDepartment',
    input,
  )
}

export async function renameDepartment(
  input: RenameDepartmentInput,
): Promise<{ success: true; key: string; version: number }> {
  return callable<RenameDepartmentInput, { success: true; key: string; version: number }>(
    'renameDepartment',
    input,
  )
}

export async function archiveDepartment(
  input: ArchiveDepartmentInput,
): Promise<{ success: true; key: string; version: number; archived: boolean }> {
  return callable<
    ArchiveDepartmentInput,
    { success: true; key: string; version: number; archived: boolean }
  >('archiveDepartment', input)
}

export async function createShift(input: CreateShiftInput): Promise<OpsShift> {
  return callable<CreateShiftInput, OpsShift>('createShift', input)
}

export async function updateShift(
  input: UpdateShiftInput,
): Promise<{ success: true; shiftId: string }> {
  return callable<UpdateShiftInput, { success: true; shiftId: string }>('updateShift', input)
}

export async function archiveShift(input: {
  workspaceId: string
  shiftId: string
  opId?: string
}): Promise<{ success: true; shiftId: string }> {
  return callable<{ workspaceId: string; shiftId: string; opId?: string }, { success: true; shiftId: string }>(
    'archiveShift',
    input,
  )
}

export async function createCoverageRule(
  input: CreateCoverageRuleInput,
): Promise<OpsCoverageRule> {
  return callable<CreateCoverageRuleInput, OpsCoverageRule>('createCoverageRule', input)
}

export async function updateCoverageRule(
  input: UpdateCoverageRuleInput,
): Promise<{ success: true; ruleId: string }> {
  return callable<UpdateCoverageRuleInput, { success: true; ruleId: string }>(
    'updateCoverageRule',
    input,
  )
}

export async function archiveCoverageRule(input: {
  workspaceId: string
  ruleId: string
  opId?: string
}): Promise<{ success: true; ruleId: string }> {
  return callable<{ workspaceId: string; ruleId: string; opId?: string }, { success: true; ruleId: string }>(
    'archiveCoverageRule',
    input,
  )
}

export async function createRoster(input: CreateRosterInput): Promise<OpsRoster> {
  return callable<CreateRosterInput, OpsRoster>('createRoster', input)
}

export async function saveDraftAssignments(
  input: SaveDraftAssignmentsInput,
): Promise<{ success: true; rosterId: string; assignmentCount: number }> {
  return callable<
    SaveDraftAssignmentsInput,
    { success: true; rosterId: string; assignmentCount: number }
  >('saveDraftAssignments', input)
}

export async function publishRoster(input: PublishRosterInput): Promise<{
  rosterId: string
  version: number
  warnings: OpsRosterWarning[]
  acks: Record<string, string>
}> {
  return callable<
    PublishRosterInput,
    {
      rosterId: string
      version: number
      warnings: OpsRosterWarning[]
      acks: Record<string, string>
    }
  >('publishRoster', input)
}

export async function previewRosterWarnings(input: PreviewRosterWarningsInput): Promise<{
  warnings: OpsRosterWarning[]
}> {
  return callable<PreviewRosterWarningsInput, { warnings: OpsRosterWarning[] }>(
    'previewRosterWarnings',
    input,
  )
}

export async function acknowledgeRoster(
  input: AcknowledgeRosterInput,
): Promise<{ success: true; rosterId: string; version: number }> {
  return callable<
    AcknowledgeRosterInput,
    { success: true; rosterId: string; version: number }
  >('acknowledgeRoster', input)
}

export async function requestRosterChange(
  input: RequestRosterChangeInput,
): Promise<{ id: string; state: string }> {
  return callable<RequestRosterChangeInput, { id: string; state: string }>(
    'requestRosterChange',
    input,
  )
}

export async function respondRosterChange(
  input: RespondRosterChangeInput,
): Promise<{ success: true; changeId: string; state: string }> {
  return callable<RespondRosterChangeInput, { success: true; changeId: string; state: string }>(
    'respondRosterChange',
    input,
  )
}

export async function decideRosterChange(input: DecideRosterChangeInput): Promise<{
  success: true
  changeId: string
  state: string
  newVersion?: number
  warnings?: OpsRosterWarning[]
}> {
  return callable<
    DecideRosterChangeInput,
    {
      success: true
      changeId: string
      state: string
      newVersion?: number
      warnings?: OpsRosterWarning[]
    }
  >('decideRosterChange', input)
}

export async function cancelRosterChange(
  input: CancelRosterChangeInput,
): Promise<{ success: true; changeId: string; state: string }> {
  return callable<CancelRosterChangeInput, { success: true; changeId: string; state: string }>(
    'cancelRosterChange',
    input,
  )
}

export async function markAttendanceRoll(
  input: MarkAttendanceRollInput,
): Promise<{ success: true; markedCount: number }> {
  return callable<MarkAttendanceRollInput, { success: true; markedCount: number }>(
    'markAttendanceRoll',
    input,
  )
}

export async function requestAttendanceCorrection(
  input: RequestAttendanceCorrectionInput,
): Promise<{ id: string; state: string }> {
  return callable<RequestAttendanceCorrectionInput, { id: string; state: string }>(
    'requestAttendanceCorrection',
    input,
  )
}

export async function decideAttendanceCorrection(
  input: DecideAttendanceCorrectionInput,
): Promise<{ success: true; correctionId: string; state: string }> {
  return callable<
    DecideAttendanceCorrectionInput,
    { success: true; correctionId: string; state: string }
  >('decideAttendanceCorrection', input)
}

export async function importAttendanceCsv(input: ImportAttendanceCsvInput): Promise<ImportAttendanceCsvResult> {
  return callable<ImportAttendanceCsvInput, ImportAttendanceCsvResult>('importAttendanceCsv', input)
}

export async function assignRelief(input: AssignReliefInput): Promise<AssignReliefResult> {
  return callable<AssignReliefInput, AssignReliefResult>('assignRelief', input)
}

export const OPS_ASSIGN_RELIEF_ERROR_MAP: Record<string, NlsKey> = {
  ON_LEAVE: 'NLSOpsSitesErrorOnLeave',
  DOUBLE_BOOKED: 'NLSOpsSitesErrorDoubleBooked',
  ROLE_CEILING: 'NLSOpsSitesErrorRoleCeiling',
  NOT_FOUND: 'NLSOpsSitesErrorNotFound',
  STALE_REQUEST: 'NLSOpsSitesErrorStaleRequest',
  MISSING_ATTRIBUTES: 'NLSOpsSitesErrorMissingAttributes',
  INACTIVE_PERSON: 'NLSOpsSitesErrorInactivePerson',
}

export function mapAssignReliefError(error: unknown): NlsKey {
  if (!error) return 'NLSOpsSitesErrorGeneric'

  const errObj =
    typeof error === 'object' && error !== null
      ? (error as Record<string, unknown>)
      : null
  const details =
    typeof errObj?.details === 'object' && errObj?.details !== null
      ? (errObj.details as Record<string, unknown>)
      : null

  const reason =
    typeof details?.reason === 'string'
      ? details.reason
      : typeof details?.code === 'string'
        ? details.code
        : typeof errObj?.code === 'string'
          ? errObj.code
          : ''

  if (reason && OPS_ASSIGN_RELIEF_ERROR_MAP[reason]) {
    return OPS_ASSIGN_RELIEF_ERROR_MAP[reason]
  }

  const message = typeof errObj?.message === 'string' ? errObj.message : String(error)
  for (const [key, nlsKey] of Object.entries(OPS_ASSIGN_RELIEF_ERROR_MAP)) {
    if (message.includes(key)) {
      return nlsKey
    }
  }

  return 'NLSOpsSitesErrorGeneric'
}

// ---------------------------------------------------------------------------
// Subscriptions (Reads)
// ---------------------------------------------------------------------------

export function subscribeDepartments(
  workspaceId: string,
  callback: (doc: OpsWorkspaceDepartmentsDoc | null) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    callback(null)
    return () => undefined
  }
  const ref = doc(firestoreDb, 'ops_departments', workspaceId)
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        callback(null)
      } else {
        callback(snap.data() as OpsWorkspaceDepartmentsDoc)
      }
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
  )
}

export function subscribeDutyRoles(
  workspaceId: string,
  callback: (roles: OpsDutyRole[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }
  const coll = collection(firestoreDb, 'ops_duty_roles')
  const q = query(coll, where('workspaceId', '==', workspaceId), orderBy('key', 'asc'))
  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => ({ ...d.data(), id: d.id }) as OpsDutyRole))
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
  )
}

export function subscribeShifts(
  workspaceId: string,
  callback: (shifts: OpsShift[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }
  const coll = collection(firestoreDb, 'ops_shifts')
  const q = query(coll, where('workspaceId', '==', workspaceId), orderBy('code', 'asc'))
  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => ({ ...d.data(), id: d.id }) as OpsShift))
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
  )
}

export function subscribeCoverageRules(
  workspaceId: string,
  placeId: string | undefined,
  callback: (rules: OpsCoverageRule[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }
  const coll = collection(firestoreDb, 'ops_coverage_rules')
  const constraints = [where('workspaceId', '==', workspaceId)]
  if (placeId) {
    constraints.push(where('placeId', '==', placeId))
  }
  const q = query(coll, ...constraints)
  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => ({ ...d.data(), id: d.id }) as OpsCoverageRule))
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
  )
}

export interface OpsSubscriptionAccessOptions {
  callerUid?: string
  isElevated?: boolean
  isManager?: boolean
  isMarker?: boolean
  isRestricted?: boolean
}

function isWorkspaceWideScope(opts: OpsSubscriptionAccessOptions | undefined): boolean {
  if (opts?.isRestricted === true) return false
  return (
    opts?.isElevated === true ||
    opts?.isManager === true ||
    opts?.isMarker === true ||
    opts?.isRestricted === false
  )
}

export function subscribeRosters(
  workspaceId: string,
  placeId: string | undefined,
  callback: (rosters: OpsRoster[]) => void,
  onError?: (error: Error) => void,
  accessScope?: OpsSubscriptionAccessOptions,
): Unsubscribe {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }
  const callerUid = accessScope?.callerUid?.trim()
  if (!callerUid) {
    onError?.(new Error('callerUid is required for subscribeRosters'))
    return () => undefined
  }
  const coll = collection(firestoreDb, 'ops_rosters')

  if (!isWorkspaceWideScope(accessScope)) {
    const qNullConstraints: QueryConstraint[] = [
      where('workspaceId', '==', workspaceId),
      where('readAccess', '==', null),
    ]
    const qUidConstraints: QueryConstraint[] = [
      where('workspaceId', '==', workspaceId),
      where('readAccess', 'array-contains', callerUid),
    ]
    if (placeId) {
      qNullConstraints.push(where('placeId', '==', placeId))
      qUidConstraints.push(where('placeId', '==', placeId))
    }
    const qNull = query(coll, ...qNullConstraints, orderBy('createdAt', 'desc'))
    const qUid = query(coll, ...qUidConstraints, orderBy('createdAt', 'desc'))

    let docsNull: OpsRoster[] = []
    let docsUid: OpsRoster[] = []

    const mergeAndEmit = () => {
      const map = new Map<string, OpsRoster>()
      for (const r of docsNull) map.set(r.id, r)
      for (const r of docsUid) map.set(r.id, r)
      const merged = Array.from(map.values())
      merged.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))
      callback(merged)
    }

    const unsubNull = onSnapshot(
      qNull,
      (snap) => {
        docsNull = snap.docs.map((d) => ({ ...d.data(), id: d.id }) as OpsRoster)
        mergeAndEmit()
      },
      (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
    )

    const unsubUid = onSnapshot(
      qUid,
      (snap) => {
        docsUid = snap.docs.map((d) => ({ ...d.data(), id: d.id }) as OpsRoster)
        mergeAndEmit()
      },
      (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
    )

    return () => {
      unsubNull()
      unsubUid()
    }
  }

  const constraints = [where('workspaceId', '==', workspaceId)]
  if (placeId) {
    constraints.push(where('placeId', '==', placeId))
  }
  const q = query(coll, ...constraints, orderBy('createdAt', 'desc'))
  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => ({ ...d.data(), id: d.id }) as OpsRoster))
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
  )
}

export function subscribeRosterVersions(
  workspaceId: string,
  rosterId: string,
  callback: (versions: OpsRosterVersion[]) => void,
  onError?: (error: Error) => void,
  accessScope?: OpsSubscriptionAccessOptions,
): Unsubscribe {
  if (!workspaceId || !rosterId) {
    callback([])
    return () => undefined
  }
  const coll = collection(firestoreDb, 'ops_rosters', rosterId, 'versions')

  if (accessScope?.isRestricted === true) {
    const callerUid = accessScope.callerUid?.trim()
    if (!callerUid) {
      onError?.(new Error('callerUid is required for restricted subscribeRosterVersions'))
      return () => undefined
    }

    const qNull = query(
      coll,
      where('workspaceId', '==', workspaceId),
      where('readAccess', '==', null),
      orderBy('version', 'desc'),
    )
    const qUid = query(
      coll,
      where('workspaceId', '==', workspaceId),
      where('readAccess', 'array-contains', callerUid),
      orderBy('version', 'desc'),
    )

    let docsNull: OpsRosterVersion[] = []
    let docsUid: OpsRosterVersion[] = []

    const mergeAndEmit = () => {
      const map = new Map<number, OpsRosterVersion>()
      for (const d of docsNull) map.set(d.version, d)
      for (const d of docsUid) map.set(d.version, d)
      const merged = Array.from(map.values())
      merged.sort((a, b) => b.version - a.version)
      callback(merged)
    }

    const unsubNull = onSnapshot(
      qNull,
      (snap) => {
        docsNull = snap.docs.map((d) => d.data() as OpsRosterVersion)
        mergeAndEmit()
      },
      (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
    )

    const unsubUid = onSnapshot(
      qUid,
      (snap) => {
        docsUid = snap.docs.map((d) => d.data() as OpsRosterVersion)
        mergeAndEmit()
      },
      (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
    )

    return () => {
      unsubNull()
      unsubUid()
    }
  }

  const q = query(
    coll,
    where('workspaceId', '==', workspaceId),
    orderBy('version', 'desc'),
  )
  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => d.data() as OpsRosterVersion))
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
  )
}

export interface SubscribeRosterChangesFilters extends OpsSubscriptionAccessOptions {
  placeId?: string
  state?: string
}

export function subscribeRosterChanges(
  workspaceId: string,
  filters: SubscribeRosterChangesFilters | undefined,
  callback: (changes: OpsRosterChange[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }
  const callerUid = filters?.callerUid?.trim()
  if (!callerUid) {
    onError?.(new Error('callerUid is required for subscribeRosterChanges'))
    return () => undefined
  }
  const coll = collection(firestoreDb, 'ops_roster_changes')
  const constraints: QueryConstraint[] = [where('workspaceId', '==', workspaceId)]
  if (!isWorkspaceWideScope(filters)) {
    constraints.push(where('partyUids', 'array-contains', callerUid))
  }
  if (filters?.placeId) {
    constraints.push(where('placeId', '==', filters.placeId))
  }
  if (filters?.state) {
    constraints.push(where('state', '==', filters.state))
  }
  const q = query(coll, ...constraints, orderBy('createdAt', 'desc'))
  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => ({ ...d.data(), id: d.id }) as OpsRosterChange))
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
  )
}

export interface SubscribeAttendanceFilters extends OpsSubscriptionAccessOptions {
  dayKey?: string
  shiftId?: string
  startDayKey?: string
  endDayKey?: string
}

export function subscribeAttendance(
  workspaceId: string,
  filters: SubscribeAttendanceFilters | undefined,
  callback: (attendance: OpsAttendance[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    callback([])
    return () => undefined
  }
  const callerUid = filters?.callerUid?.trim()
  if (!callerUid) {
    onError?.(new Error('callerUid is required for subscribeAttendance'))
    return () => undefined
  }
  const coll = collection(firestoreDb, 'ops_attendance')
  const constraints: QueryConstraint[] = [where('workspaceId', '==', workspaceId)]
  if (!isWorkspaceWideScope(filters)) {
    constraints.push(where('personUid', '==', callerUid))
  }
  if (filters?.dayKey) {
    constraints.push(where('dayKey', '==', filters.dayKey))
  } else if (filters?.startDayKey && filters?.endDayKey) {
    constraints.push(where('dayKey', '>=', filters.startDayKey))
    constraints.push(where('dayKey', '<=', filters.endDayKey))
  }
  if (filters?.shiftId) {
    constraints.push(where('shiftId', '==', filters.shiftId))
  }
  const q = query(coll, ...constraints)
  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => ({ ...d.data(), id: d.id }) as OpsAttendance))
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
  )
}

export function subscribeAttendanceCorrections(
  attendanceId: string,
  callback: (corrections: OpsAttendanceCorrection[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  if (!attendanceId) {
    callback([])
    return () => undefined
  }
  const coll = collection(firestoreDb, 'ops_attendance', attendanceId, 'corrections')
  const q = query(coll, orderBy('requestedAt', 'asc'))
  return onSnapshot(
    q,
    (snap) => {
      callback(snap.docs.map((d) => ({ ...d.data(), id: d.id }) as OpsAttendanceCorrection))
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err))),
  )
}
